import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { PiUserPlusDuotone, PiXDuotone } from 'react-icons/pi';
import { getFriends } from '../../../api/friendService';
import { addRoomFriends, getRoomMembers } from '../../../api/roomService';
import { publicUserName, safeUsername } from '../../utils/userDisplay';
import Modal from '../../common/Modal';
import Button from '../../common/Button';

const FRIENDS_LOAD_ERROR = 'Не удалось загрузить список друзей';
const MEMBERS_LOAD_ERROR = 'Не удалось загрузить участников комнаты';
const ADD_FRIENDS_ERROR = 'Не удалось добавить друзей в комнату';
const EMPTY_FRIENDS_MESSAGE = 'Нет доступных друзей для добавления. Участники комнаты уже исключены из списка.';
const SELECT_FRIENDS_MESSAGE = 'Выбери хотя бы одного друга';
const SUCCESS_MESSAGE_TEMPLATE = 'Добавлено друзей: ';
const ALREADY_MEMBER_MESSAGE_TEMPLATE = 'Уже были в комнате: ';
const INVALID_FRIEND_MESSAGE_TEMPLATE = 'Не удалось добавить: ';

export default function AddRoomFriendsModal({ isOpen, onClose, roomId, onAdded }) {
  const [friends, setFriends] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedFriendIds, setSelectedFriendIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const memberIds = useMemo(
    () => new Set(members.map((member) => member.userId)),
    [members]
  );

  const availableFriends = useMemo(
    () => friends.filter((friend) => !memberIds.has(friend.id)),
    [friends, memberIds]
  );

  const selectedFriends = useMemo(
    () => availableFriends.filter((friend) => selectedFriendIds.includes(friend.id)),
    [availableFriends, selectedFriendIds]
  );

  const loadData = useCallback(async ({ keepSuccess = false } = {}) => {
    if (!roomId) return;
    setLoading(true);
    setError('');
    if (!keepSuccess) setSuccess('');
    try {
      const [friendsData, membersData] = await Promise.all([getFriends(), getRoomMembers(roomId)]);
      setFriends(Array.isArray(friendsData) ? friendsData : []);
      setMembers(Array.isArray(membersData) ? membersData : []);
      setSelectedFriendIds([]);
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 403 || status === 404 ? MEMBERS_LOAD_ERROR : FRIENDS_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  const toggleFriend = (friendId) => {
    setSelectedFriendIds((current) => (
      current.includes(friendId)
        ? current.filter((id) => id !== friendId)
        : [...current, friendId]
    ));
  };

  const removeFriend = (friendId) => {
    setSelectedFriendIds((current) => current.filter((id) => id !== friendId));
  };

  const friendNames = (friendIds) => friendIds
    .map((friendId) => friends.find((friend) => friend.id === friendId))
    .filter(Boolean)
    .map(publicUserName)
    .join(', ');

  const buildResultMessage = (result) => {
    const parts = [];
    const addedCount = result?.addedMembers?.length || 0;
    const alreadyMemberIds = result?.alreadyMemberIds || [];
    const invalidFriendIds = result?.invalidFriendIds || [];

    if (addedCount > 0) parts.push(`${SUCCESS_MESSAGE_TEMPLATE}${addedCount}`);
    if (alreadyMemberIds.length > 0) parts.push(`${ALREADY_MEMBER_MESSAGE_TEMPLATE}${friendNames(alreadyMemberIds)}`);
    if (invalidFriendIds.length > 0) parts.push(`${INVALID_FRIEND_MESSAGE_TEMPLATE}${friendNames(invalidFriendIds) || invalidFriendIds.join(', ')}`);
    return parts.join('. ');
  };

  const handleSubmit = async () => {
    if (selectedFriendIds.length === 0) {
      setError(SELECT_FRIENDS_MESSAGE);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const result = await addRoomFriends(roomId, selectedFriendIds);
      setSuccess(buildResultMessage(result));
      setSelectedFriendIds([]);
      await loadData({ keepSuccess: true });
      if (result?.addedMembers?.length > 0) onAdded?.(result.addedMembers);
    } catch (err) {
      setError(err?.response?.data?.message || ADD_FRIENDS_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Добавить друзей" size="md">
      <div className="bm-add-room-friends">
        <p className="bm-text-secondary bm-text-sm">
          Выбери одного или нескольких друзей. Уже добавленные участники комнаты скрыты из списка.
        </p>

        {error && <div className="bm-auth-error">{error}</div>}
        {success && <div className="bm-auth-success">{success}</div>}

        {selectedFriends.length > 0 && (
          <div className="bm-friend-picker__selected" aria-label="Выбранные друзья">
            {selectedFriends.map((friend) => (
              <button
                key={friend.id}
                type="button"
                className="bm-friend-chip"
                onClick={() => removeFriend(friend.id)}
                aria-label={`Убрать ${publicUserName(friend)} из приглашений`}
              >
                <span>{publicUserName(friend)}</span>
                <PiXDuotone size={14} aria-hidden="true" />
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="bm-text-secondary bm-text-sm">Загрузка...</p>
        ) : availableFriends.length > 0 ? (
          <div className="bm-friend-picker__list bm-add-room-friends__list">
            {availableFriends.map((friend) => (
              <FriendOption
                key={friend.id}
                friend={friend}
                checked={selectedFriendIds.includes(friend.id)}
                onToggle={() => toggleFriend(friend.id)}
              />
            ))}
          </div>
        ) : (
          <p className="bm-text-secondary bm-text-sm" style={{ textAlign: 'center', padding: '16px 0' }}>
            {EMPTY_FRIENDS_MESSAGE}
          </p>
        )}

        <div className="bm-modal__footer" style={{ margin: '0 calc(var(--bm-space-lg) * -1) calc(var(--bm-space-lg) * -1)' }}>
          <Button variant="ghost" type="button" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            variant="primary"
            type="button"
            icon={<PiUserPlusDuotone size={16} />}
            loading={submitting}
            disabled={loading || selectedFriendIds.length === 0}
            onClick={handleSubmit}
          >
            Добавить выбранных
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function FriendOption({ friend, checked, onToggle }) {
  return (
    <label className={`bm-check-card${checked ? ' bm-check-card--checked' : ''}`}>
      <input
        className="bm-check-card__input"
        type="checkbox"
        checked={checked}
        onChange={onToggle}
      />
      <span className="bm-check-card__box" aria-hidden="true" />
      <span className="bm-check-card__content">
        <span className="bm-check-card__name">{publicUserName(friend)}</span>
        {safeUsername(friend) && <span className="bm-check-card__hint">@{safeUsername(friend)}</span>}
      </span>
    </label>
  );
}
